import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Paginator } from 'primereact/paginator';
import styled from 'styled-components';
import NotificationsService, { NotificationModel } from 'services/NotificationsService';
import { notificationUnreadCountQueryKey } from 'hooks/useUnreadNotificationCount';
import useUnreadNotificationCount from 'hooks/useUnreadNotificationCount';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import { Container } from 'styles/Styles.styled';
import { H1NewUI } from 'styles/v2/Styles.styled';

const PAGE_SIZE = 20;

const CATEGORY_TABS: { label: string; value?: string; icon: string }[] = [
  { label: 'All', value: undefined, icon: 'pi pi-inbox' },
  { label: 'Release notes', value: 'ReleaseNotes', icon: 'pi pi-tag' },
  { label: 'News', value: 'News', icon: 'pi pi-globe' },
  { label: 'Warnings', value: 'Warning', icon: 'pi pi-exclamation-triangle' },
];

const NotificationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [opened, setOpened] = useState<NotificationModel | null>(null);

  const queryClient = useQueryClient();
  const unread = useUnreadNotificationCount();

  const listKey = ['notifications', 'list', activeTab ?? 'all', page] as const;

  const { data, isLoading } = useQuery({
    queryKey: listKey,
    queryFn: () => NotificationsService.list(activeTab, page, PAGE_SIZE),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    queryClient.invalidateQueries({ queryKey: notificationUnreadCountQueryKey });
  };

  const markAsRead = useMutation({
    mutationFn: (id: string) => NotificationsService.markAsRead(id),
    onSuccess: invalidateAll,
  });

  const markAllAsRead = useMutation({
    mutationFn: () => NotificationsService.markAllAsRead(activeTab),
    onSuccess: invalidateAll,
  });

  const handleOpen = (item: NotificationModel) => {
    setOpened(item);
    if (!item.isRead) {
      markAsRead.mutate(item.id);
    }
  };

  const totalUnreadOnPage = useMemo(() => data?.items.filter((i) => !i.isRead).length ?? 0, [data]);

  const unreadByCategory = unread?.byCategory ?? {};
  const unreadTotal = unread?.total ?? 0;

  return (
    <Container>
      <PageHeader>
        <H1NewUI>Notifications</H1NewUI>
        <Button
          label="Mark all as read"
          icon="pi pi-check"
          className="p-button-text"
          disabled={totalUnreadOnPage === 0 || markAllAsRead.isPending}
          onClick={() => markAllAsRead.mutate()}
        />
      </PageHeader>

      <ContentsGrid>
        <SideMenu>
          {CATEGORY_TABS.map((t) => {
            const isActive = activeTab === t.value;
            const count = t.value ? unreadByCategory[t.value] ?? 0 : unreadTotal;
            return (
              <MenuItem
                key={t.label}
                type="button"
                $active={isActive}
                onClick={() => {
                  setActiveTab(t.value);
                  setPage(1);
                }}
              >
                <i className={t.icon} />
                <span>{t.label}</span>
                {count > 0 && <CountBadge>{count}</CountBadge>}
              </MenuItem>
            );
          })}
        </SideMenu>

        <MainColumn>
          {isLoading && (
            <SpinnerWrap>
              <ProgressSpinner />
            </SpinnerWrap>
          )}

          {!isLoading && data && (
            <>
              {data.items.length === 0 ? (
                <EmptyState>No notifications.</EmptyState>
              ) : (
                <List>
                  {data.items.map((item) => (
                    <ListItem key={item.id} $unread={!item.isRead} onClick={() => handleOpen(item)}>
                      <ItemRow>
                        {!item.isRead && <UnreadDot />}
                        <ItemTitle $unread={!item.isRead}>{item.title}</ItemTitle>
                      </ItemRow>
                      <ItemMeta>
                        <span>{new Date(item.publishedAt).toLocaleString()}</span>
                        <CategoryChip>{item.category}</CategoryChip>
                      </ItemMeta>
                    </ListItem>
                  ))}
                </List>
              )}

              {data.total > PAGE_SIZE && (
                <Paginator
                  first={(page - 1) * PAGE_SIZE}
                  rows={PAGE_SIZE}
                  totalRecords={data.total}
                  onPageChange={(e) => setPage((e.page ?? 0) + 1)}
                />
              )}
            </>
          )}
        </MainColumn>
      </ContentsGrid>

      <Dialog
        visible={!!opened}
        onHide={() => setOpened(null)}
        header={opened?.title}
        style={{ width: '640px', maxWidth: '92vw' }}
        modal
      >
        {opened && (
          <DialogBody>
            <DetailMeta>
              <span>{new Date(opened.publishedAt).toLocaleString()}</span>
              <CategoryChip>{opened.category}</CategoryChip>
            </DetailMeta>
            <DetailBody dangerouslySetInnerHTML={{ __html: opened.body }} />
          </DialogBody>
        )}
      </Dialog>
    </Container>
  );
};

export default withGreyBackground(NotificationsPage);

const PageHeader = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 1.5rem 0;
`;

const ContentsGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 2rem;
  row-gap: 1rem;

  ${(props) => props.theme.media.desktop} {
    grid-template-columns: max-content minmax(0, 1fr);
  }
`;

const SideMenu = styled.div`
  min-width: 252px;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  border-radius: 4px;
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.04);
  padding: 1rem;
  background-color: ${(props) => props.theme.colors.primaryWhite};
  height: fit-content;
`;

const MenuItem = styled.button<{ $active?: boolean }>`
  padding: 12px 16px;
  line-height: 1;
  color: ${(props) => (props.$active ? props.theme.colors.primaryGreen : props.theme.colors.primaryBlack)};
  display: flex;
  gap: 1em;
  align-items: center;
  background-color: ${(props) =>
    props.$active ? props.theme.colors.secondaryLightGreen30 : props.theme.colors.primaryWhite};
  text-decoration: none;
  border: none;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  text-align: left;
  border-radius: 4px;

  > span {
    flex: 1;
  }

  &:hover,
  &:focus-visible {
    color: ${(props) => props.theme.colors.primaryBlack};
    background-color: ${(props) => props.theme.colors.statusLabelGrayBg ?? '#f4f4f4'};
  }
`;

const CountBadge = styled.span`
  background: ${(props) => props.theme.colors.statusLabelRedText};
  color: white;
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 11px;
  min-width: 22px;
  text-align: center;
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SpinnerWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const EmptyState = styled.div`
  padding: 32px;
  text-align: center;
  color: ${(props) => props.theme.colors.darkGray ?? '#666'};
  background: ${(props) => props.theme.colors.primaryWhite};
  border-radius: 6px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ListItem = styled.div<{ $unread: boolean }>`
  background: ${(props) => props.theme.colors.primaryWhite};
  border-left: 3px solid ${(props) => (props.$unread ? props.theme.colors.statusLabelRedText : 'transparent')};
  padding: 12px 16px;
  border-radius: 4px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  &:hover {
    background: ${(props) => props.theme.colors.statusLabelGrayBg ?? '#fafafa'};
  }
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UnreadDot = styled.div`
  height: 8px;
  width: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.statusLabelRedText};
  flex-shrink: 0;
`;

const ItemTitle = styled.div<{ $unread: boolean }>`
  font-weight: ${(props) => (props.$unread ? 600 : 400)};
  font-size: 14px;
  color: ${(props) => props.theme.colors.primaryBlack};
`;

const ItemMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
  font-size: 12px;
  color: ${(props) => props.theme.colors.darkGray ?? '#666'};
`;

const CategoryChip = styled.span`
  background: ${(props) => props.theme.colors.statusLabelGrayBg ?? '#eef3ed'};
  color: ${(props) => props.theme.colors.primaryBlack};
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
`;

const DialogBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 1.5rem 1.5rem 1.5rem;
`;

const DetailMeta = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  font-size: 12px;
  color: ${(props) => props.theme.colors.darkGray ?? '#666'};
`;

const DetailBody = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${(props) => props.theme.colors.primaryBlack};

  p {
    margin: 0 0 8px 0;
  }
`;
